import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  HStack,
  Badge,
  Button,
  VStack,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  InputGroup,
  InputLeftElement,
  Select,
  Checkbox,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { NumericFormat } from 'react-number-format';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, FileText, Clock, Warning, CaretDown, MagnifyingGlass } from 'phosphor-react';
import { toast } from 'sonner';
import moment from 'moment';
import {
  useEventos,
  IEventoContrato,
  IEventoCategoria,
  IEventoServico,
} from '../../../../hooks/useEventos';
import { useTemplateDoEvento } from '../../../../hooks/useEventoContrato';
import { TIPO_CONTRATO_PADRAO } from '../contrato/tiposDeContrato';
import SelectTipoContrato from '../contrato/SelectTipoContrato';
import {
  IRelatorioAssinaturas,
  RELATORIO_PADRAO,
  SecaoRelatorio,
  relatorioParaApi,
} from '../contrato/RelatorioAssinaturas';
import { IDataNoPagination } from '../../../../types/shared';
import { formatCurrency } from '../../../../utils/FormatFunctions';
import { KanbanDatePicker } from '../../../../components/kanban/KanbanDatePicker';

const STATUS_CONFIG: Record<
  string,
  { color: string; label: string; next?: string; nextLabel?: string; step: number }
> = {
  proposta: {
    color: 'yellow',
    label: 'Proposta',
    next: 'contrato',
    nextLabel: '+ Contrato',
    step: 1,
  },
  contrato: {
    color: 'blue',
    label: 'Contrato',
    next: 'enviado_assinatura',
    nextLabel: 'Enviar p/ Ass.',
    step: 2,
  },
  enviado_assinatura: {
    color: 'orange',
    label: 'Enviado p/ Ass.',
    next: 'assinado',
    nextLabel: 'Assinar',
    step: 3,
  },
  assinado: {
    color: 'cyan',
    label: 'Assinado',
    next: 'nf_emitida',
    nextLabel: '→ NF Emitida',
    step: 4,
  },
  aguardando_nf: { color: 'teal', label: 'Aguardando NF', step: 5 },
  nf_emitida: { color: 'purple', label: 'NF Emitida', next: 'pago', nextLabel: '+ Pago', step: 6 },
  pago: { color: 'green', label: 'Pago', step: 7 },
  cancelado: { color: 'gray', label: 'Cancelado', step: 8 },
};

const POR_PAGINA = 50;
const SEM_CATEGORIA = 'Sem categoria';

// Etapa = coluna do quadro em que o card está; sem card, o status do contrato. Agrupa sem diferença de caixa.
const nomeDaEtapa = (c: IEventoContrato) =>
  c.kanban_coluna ?? STATUS_CONFIG[c.status]?.label ?? c.status;
const etapaDe = (c: IEventoContrato) => nomeDaEtapa(c).toUpperCase();
const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
// Pago ou cancelado não vence mais: sem prazo na linha e fora de "Vencidos".
const diasAteVencer = (c: IEventoContrato) =>
  c.dt_vencimento && c.status !== 'pago' && c.status !== 'cancelado'
    ? moment(c.dt_vencimento).diff(moment(), 'days')
    : null;
const vencimentoCor = (dias: number) => {
  if (dias < 0) return 'red.500';
  if (dias < 7) return 'orange.500';
  return 'gray.400';
};

interface ContratosTabProps {
  eventoId: number;
}

export default function ContratosTab({ eventoId }: ContratosTabProps) {
  const queryClient = useQueryClient();
  const { getContratos, createContrato, getCategorias, getServicos } = useEventos();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [fornecedorNome, setFornecedorNome] = useState('');
  const [valor, setValor] = useState('');
  const [categoriaId, setCategoriaId] = useState<number | ''>('');
  const [selectedServicoIds, setSelectedServicoIds] = useState<number[]>([]);
  const [dtVencimento, setDtVencimento] = useState('');
  const [observacao, setObservacao] = useState('');
  const [relatorio, setRelatorio] = useState<IRelatorioAssinaturas>(RELATORIO_PADRAO);
  const [contratoTipo, setContratoTipo] = useState(TIPO_CONTRATO_PADRAO);
  // O catálogo de tipos e o estado do template de cada um vêm do template do evento.
  const { data: templateData } = useTemplateDoEvento(eventoId, isOpen);
  const tiposContrato = templateData?.data?.tipos ?? [];

  const { data: contratosData, isLoading } = useQuery<IDataNoPagination<IEventoContrato[]>>({
    queryKey: ['evento-contratos', eventoId],
    queryFn: getContratos,
  });

  const { data: categoriasData } = useQuery({
    queryKey: ['evento-categorias'],
    queryFn: getCategorias,
    enabled: isOpen,
  });

  const { data: servicosData } = useQuery<IDataNoPagination<IEventoServico[]>>({
    queryKey: ['evento-servicos'],
    queryFn: getServicos,
    enabled: isOpen,
  });

  const contratos = contratosData?.data ?? [];
  const categorias: IEventoCategoria[] = (categoriasData as any)?.data ?? [];
  const allServicos: IEventoServico[] = servicosData?.data ?? [];
  const filteredServicos = categoriaId
    ? allServicos.filter(s => s.categoria_id === categoriaId)
    : allServicos;

  const [busca, setBusca] = useState('');
  const [etapa, setEtapa] = useState('');
  const [categoria, setCategoria] = useState('');
  const [soVencidos, setSoVencidos] = useState(false);
  const [limite, setLimite] = useState(POR_PAGINA);
  useEffect(() => setLimite(POR_PAGINA), [busca, etapa, categoria, soVencidos]);

  // Onde estão os contratos: uma etapa por coluna do quadro, na ordem do status.
  const etapas: { nome: string; rotulo: string; n: number; passo: number; cor: string }[] = [];
  contratos.forEach(c => {
    const nome = etapaDe(c);
    const cfg = STATUS_CONFIG[c.status];
    const passo = cfg?.step ?? 99;
    const e = etapas.find(x => x.nome === nome);
    if (!e) etapas.push({ nome, rotulo: nomeDaEtapa(c), n: 1, passo, cor: cfg?.color ?? 'gray' });
    else {
      e.n += 1;
      if (passo < e.passo) Object.assign(e, { passo, cor: cfg?.color ?? 'gray' });
    }
  });
  etapas.sort((a, b) => a.passo - b.passo || a.nome.localeCompare(b.nome));
  const categoriasDoEvento = Array.from(
    new Set(contratos.map(c => c.categoria_nome || SEM_CATEGORIA)),
  ).sort();
  const vencidos = contratos.filter(c => (diasAteVencer(c) ?? 0) < 0).length;
  // Etapa ou categoria que sumiu num refetch (card movido no quadro) deixa de filtrar.
  const etapaAtiva = etapas.some(e => e.nome === etapa) ? etapa : '';
  const categoriaAtiva = categoriasDoEvento.includes(categoria) ? categoria : '';
  // e o filtro é zerado: não volta sozinho se um contrato voltar para a etapa num refetch seguinte
  useEffect(() => {
    if (etapa && !etapaAtiva) setEtapa('');
    if (categoria && !categoriaAtiva) setCategoria('');
  }, [etapa, etapaAtiva, categoria, categoriaAtiva]);

  const termo = semAcento(busca.trim());
  // CNPJ só quando a busca é número (com ou sem pontuação): "Buffet 2026" não casa CNPJ com 2026.
  const digitos = /^[\d.\-/\s]+$/.test(busca.trim()) ? busca.replace(/\D/g, '') : '';
  const filtrados = contratos.filter(
    c =>
      (!etapaAtiva || etapaDe(c) === etapaAtiva) &&
      (!categoriaAtiva || (c.categoria_nome || SEM_CATEGORIA) === categoriaAtiva) &&
      (!soVencidos || (diasAteVencer(c) ?? 0) < 0) &&
      (!termo ||
        semAcento(
          [c.fornecedor_nome, c.categoria_nome, ...(c.servicos ?? []).map(s => s.nome)].join(' '),
        ).includes(termo) ||
        (digitos.length >= 3 &&
          (c.fornecedor_documento ?? '').replace(/\D/g, '').includes(digitos))),
  );
  const comFiltro = !!(termo || etapaAtiva || categoriaAtiva || soVencidos);
  const limparFiltros = () => {
    setBusca('');
    setEtapa('');
    setCategoria('');
    setSoVencidos(false);
  };

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['evento-contratos', eventoId] });
    queryClient.invalidateQueries({ queryKey: ['evento-detalhe', eventoId] });
    queryClient.invalidateQueries({ queryKey: ['eventos'] });
    queryClient.invalidateQueries({ queryKey: ['eventos-alertas'] });
    queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
  };

  const createMut = useMutation({
    mutationFn: createContrato,
    onSuccess: () => {
      invalidateAll();
      toast.success('Fornecedor adicionado!');
      onClose();
      resetForm();
    },
    onError: () => toast.error('Erro ao adicionar'),
  });

  const resetForm = () => {
    setFornecedorNome('');
    setValor('');
    setCategoriaId('');
    setSelectedServicoIds([]);
    setDtVencimento('');
    setObservacao('');
    setContratoTipo(TIPO_CONTRATO_PADRAO);
    setRelatorio(RELATORIO_PADRAO);
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleCreate = () => {
    if (!fornecedorNome || !valor) return;
    createMut.mutate({
      eventoId,
      data: {
        fornecedor_nome: fornecedorNome,
        valor: Number(valor),
        categoria_id: categoriaId ? Number(categoriaId) : undefined,
        servico_ids: selectedServicoIds.length > 0 ? selectedServicoIds : undefined,
        dt_vencimento: dtVencimento || undefined,
        observacao: observacao || undefined,
        contrato_tipo: contratoTipo,
        ...relatorioParaApi(relatorio),
      },
    });
  };

  if (isLoading) {
    return (
      <Flex justify="center" py={10}>
        <Spinner size="md" color="gray.300" thickness="3px" />
      </Flex>
    );
  }

  const filtroBotao = (ativo: boolean) => ({
    size: 'xs' as const,
    borderRadius: 'full',
    fontWeight: '500',
    border: '1px solid',
    borderColor: ativo ? 'gray.900' : 'gray.200',
    bg: ativo ? 'gray.900' : 'white',
    color: ativo ? 'white' : 'gray.600',
    _hover: { bg: ativo ? 'gray.800' : 'gray.50' },
  });

  return (
    <Box>
      {contratos.length > 0 && (
        <Box
          bg="white"
          borderRadius="14px"
          border="1px solid"
          borderColor="gray.100"
          px={4}
          py={3}
          mb={3}
        >
          <Text fontSize="12px" fontWeight="500" color="gray.500" mb={2}>
            Onde estão os contratos
          </Text>
          <Flex h="10px" gap="2px" borderRadius="full" overflow="hidden" mb={3}>
            {etapas.map(e => (
              <Box key={e.nome} flex={e.n} bg={`${e.cor}.400`} title={`${e.rotulo}: ${e.n}`} />
            ))}
          </Flex>
          <Flex gap={1.5} flexWrap="wrap">
            <Button {...filtroBotao(!etapaAtiva)} onClick={() => setEtapa('')}>
              Todas {contratos.length}
            </Button>
            {etapas.map(e => (
              <Button
                key={e.nome}
                {...filtroBotao(etapaAtiva === e.nome)}
                aria-pressed={etapaAtiva === e.nome}
                leftIcon={<Box w="8px" h="8px" borderRadius="full" bg={`${e.cor}.400`} />}
                onClick={() => setEtapa(etapaAtiva === e.nome ? '' : e.nome)}
              >
                {e.rotulo} {e.n}
              </Button>
            ))}
          </Flex>
        </Box>
      )}

      <Flex gap={2} mb={3} align="center" flexWrap="wrap">
        {contratos.length > 0 && (
          <>
            <InputGroup size="sm" maxW={{ base: '100%', md: '320px' }}>
              <InputLeftElement pointerEvents="none">
                <MagnifyingGlass size={14} color="#A0AEC0" />
              </InputLeftElement>
              <Input
                bg="white"
                borderRadius="10px"
                fontSize="13px"
                value={busca}
                onChange={e => setBusca(e.target.value)}
                placeholder="Buscar fornecedor, CNPJ ou serviço"
                aria-label="Buscar contrato"
              />
            </InputGroup>
            <Select
              size="sm"
              w="auto"
              bg="white"
              borderRadius="10px"
              fontSize="13px"
              value={categoriaAtiva}
              onChange={e => setCategoria(e.target.value)}
              aria-label="Categoria"
            >
              <option value="">Todas as categorias</option>
              {categoriasDoEvento.map(nome => (
                <option key={nome} value={nome}>
                  {nome}
                </option>
              ))}
            </Select>
            <Checkbox
              size="sm"
              isChecked={soVencidos}
              onChange={e => setSoVencidos(e.target.checked)}
              isDisabled={!vencidos && !soVencidos}
            >
              <Text as="span" fontSize="13px" color="gray.600">
                Vencidos ({vencidos})
              </Text>
            </Checkbox>
            {comFiltro && (
              <Button size="xs" variant="link" color="gray.500" onClick={limparFiltros}>
                Limpar filtros
              </Button>
            )}
          </>
        )}
        <Button
          size="xs"
          bg="gray.900"
          color="white"
          borderRadius="full"
          fontWeight="500"
          ml="auto"
          leftIcon={<Plus size={12} weight="bold" />}
          onClick={onOpen}
          _hover={{ bg: 'gray.800' }}
        >
          Adicionar Fornecedor
        </Button>
      </Flex>

      {contratos.length > 0 && (
        <Box
          bg="white"
          borderRadius="14px"
          border="1px solid"
          borderColor="gray.100"
          overflowX="auto"
        >
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Fornecedor</Th>
                <Th>Categoria</Th>
                <Th isNumeric>Valor</Th>
                <Th>Etapa</Th>
                <Th>Vencimento</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtrados.slice(0, limite).map(c => {
                const cfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.proposta;
                const dias = diasAteVencer(c);
                return (
                  <Tr key={c.id} _hover={{ bg: 'gray.50' }}>
                    <Td maxW="360px">
                      <Text
                        fontSize="13px"
                        fontWeight="600"
                        color="gray.800"
                        noOfLines={1}
                        title={
                          c.servicos?.length
                            ? `${c.fornecedor_nome}: ${c.servicos.map(s => s.nome).join(', ')}`
                            : c.fornecedor_nome
                        }
                      >
                        {c.fornecedor_nome}
                      </Text>
                    </Td>
                    <Td>
                      <HStack spacing={1.5}>
                        <Box
                          w="8px"
                          h="8px"
                          borderRadius="full"
                          flexShrink={0}
                          bg={c.categoria_cor || 'gray.300'}
                        />
                        <Text fontSize="12px" color="gray.600" noOfLines={1}>
                          {c.categoria_nome || SEM_CATEGORIA}
                        </Text>
                      </HStack>
                    </Td>
                    <Td isNumeric fontSize="13px" color="gray.700" whiteSpace="nowrap">
                      {formatCurrency(c.valor)}
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={cfg.color}
                        variant={c.status === 'pago' ? 'solid' : 'subtle'}
                        fontSize="10px"
                        fontWeight="600"
                        borderRadius="md"
                        px={2}
                        py={0.5}
                      >
                        {nomeDaEtapa(c)}
                      </Badge>
                    </Td>
                    <Td whiteSpace="nowrap">
                      {dias === null ? (
                        <Text fontSize="12px" color="gray.300">
                          —
                        </Text>
                      ) : (
                        <HStack spacing={1} color={vencimentoCor(dias)}>
                          {dias < 0 ? <Warning size={12} /> : <Clock size={12} />}
                          <Text fontSize="12px" fontWeight="500">
                            {dias < 0 ? `${Math.abs(dias)}d atrasado` : `${dias}d`}
                          </Text>
                        </HStack>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
          {filtrados.length === 0 ? (
            <Flex direction="column" align="center" gap={2} py={8}>
              <Text fontSize="13px" color="gray.400">
                Nenhum contrato com esses filtros.
              </Text>
              <Button size="xs" variant="link" color="gray.600" onClick={limparFiltros}>
                Limpar filtros
              </Button>
            </Flex>
          ) : (
            <Flex
              justify="space-between"
              align="center"
              px={4}
              py={2.5}
              borderTop="1px solid"
              borderColor="gray.100"
            >
              <Text fontSize="12px" color="gray.500">
                Mostrando {Math.min(limite, filtrados.length)} de {filtrados.length}
              </Text>
              {filtrados.length > limite && (
                <Button
                  size="xs"
                  variant="outline"
                  borderRadius="full"
                  onClick={() => setLimite(limite + POR_PAGINA)}
                >
                  Mostrar mais
                </Button>
              )}
            </Flex>
          )}
        </Box>
      )}

      {contratos.length === 0 && (
        <Flex
          direction="column"
          align="center"
          py={12}
          gap={3}
          bg="white"
          borderRadius="14px"
          border="1px dashed"
          borderColor="gray.200"
        >
          <Flex w="48px" h="48px" borderRadius="14px" bg="gray.50" align="center" justify="center">
            <FileText size={22} color="#CBD5E0" weight="light" />
          </Flex>
          <Text color="gray.400" fontSize="13px">
            Nenhum fornecedor adicionado
          </Text>
          <Button
            size="xs"
            bg="gray.900"
            color="white"
            borderRadius="full"
            onClick={onOpen}
            _hover={{ bg: 'gray.800' }}
          >
            <Plus size={12} style={{ marginRight: 4 }} />
            Adicionar
          </Button>
        </Flex>
      )}

      {/* Modal criar contrato */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="16px">
          <ModalHeader fontSize="16px" fontWeight="700" pb={2}>
            Adicionar Fornecedor
          </ModalHeader>
          <ModalCloseButton borderRadius="full" />
          <ModalBody>
            <VStack spacing={3}>
              <FormControl isRequired>
                <FormLabel fontSize="12px" fontWeight="600" color="gray.500">
                  Fornecedor
                </FormLabel>
                <Input
                  size="sm"
                  borderRadius="10px"
                  value={fornecedorNome}
                  onChange={e => setFornecedorNome(e.target.value)}
                  placeholder="Nome do fornecedor"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="12px" fontWeight="600" color="gray.500">
                  Valor
                </FormLabel>
                <Input
                  as={NumericFormat}
                  size="sm"
                  borderRadius="10px"
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  prefix="R$ "
                  value={valor ? parseFloat(valor) : ''}
                  onValueChange={({ floatValue }: any) => setValor(String(floatValue ?? ''))}
                  placeholder="R$ 0,00"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="12px" fontWeight="600" color="gray.500">
                  Tipo de contrato
                </FormLabel>
                <SelectTipoContrato
                  tipos={tiposContrato}
                  value={contratoTipo}
                  onChange={setContratoTipo}
                  mostrarEstado
                  borderRadius="10px"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="12px" fontWeight="600" color="gray.500">
                  Categoria
                </FormLabel>
                <Menu matchWidth>
                  <MenuButton
                    as={Button}
                    size="sm"
                    variant="outline"
                    borderRadius="10px"
                    fontWeight="400"
                    fontSize="13px"
                    color={categoriaId ? 'gray.700' : 'gray.400'}
                    textAlign="left"
                    w="100%"
                    rightIcon={<CaretDown size={12} />}
                    _hover={{ borderColor: 'gray.300' }}
                    _active={{ bg: 'white' }}
                  >
                    {categoriaId
                      ? categorias.find(c => c.id === categoriaId)?.nome || 'Selecione...'
                      : 'Selecione...'}
                  </MenuButton>
                  <MenuList
                    borderRadius="12px"
                    boxShadow="0 8px 24px rgba(0,0,0,0.12)"
                    border="1px solid"
                    borderColor="gray.100"
                    py={1}
                    zIndex={10}
                  >
                    {categorias.map(cat => (
                      <MenuItem
                        key={cat.id}
                        fontSize="13px"
                        py={2}
                        px={3}
                        _hover={{ bg: 'gray.50' }}
                        onClick={() => {
                          setCategoriaId(cat.id);
                          setSelectedServicoIds([]);
                        }}
                        icon={
                          <Box w="8px" h="8px" borderRadius="full" bg={cat.cor || 'gray.300'} />
                        }
                      >
                        {cat.nome}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </FormControl>
              {filteredServicos.length > 0 && (
                <FormControl>
                  <FormLabel fontSize="12px" fontWeight="600" color="gray.500">
                    Servicos
                  </FormLabel>
                  <Flex gap={1.5} flexWrap="wrap">
                    {filteredServicos.map(s => {
                      const selected = selectedServicoIds.includes(s.id);
                      return (
                        <Badge
                          key={s.id}
                          cursor="pointer"
                          fontSize="11px"
                          fontWeight="500"
                          px={2.5}
                          py={1}
                          borderRadius="full"
                          bg={selected ? 'blue.500' : 'gray.100'}
                          color={selected ? 'white' : 'gray.600'}
                          _hover={{
                            bg: selected ? 'blue.600' : 'gray.200',
                          }}
                          transition="all 0.1s ease"
                          textTransform="none"
                          onClick={() =>
                            setSelectedServicoIds(prev =>
                              selected ? prev.filter(id => id !== s.id) : [...prev, s.id],
                            )
                          }
                        >
                          {s.nome}
                        </Badge>
                      );
                    })}
                  </Flex>
                </FormControl>
              )}
              <FormControl>
                <FormLabel fontSize="12px" fontWeight="600" color="gray.500">
                  Vencimento
                </FormLabel>
                <KanbanDatePicker
                  value={dtVencimento}
                  onChange={setDtVencimento}
                  placeholder="Selecionar data"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="12px" fontWeight="600" color="gray.500">
                  Observação
                </FormLabel>
                <Textarea
                  size="sm"
                  borderRadius="10px"
                  value={observacao}
                  onChange={e => setObservacao(e.target.value)}
                  rows={2}
                />
              </FormControl>
              {/* Box, e não FormControl: o componente tem 3 campos e o FormControl daria o mesmo id a todos. */}
              <Box w="100%">
                <SecaoRelatorio value={relatorio} onChange={setRelatorio} />
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter pt={2}>
            <Button size="sm" variant="ghost" mr={2} onClick={onClose} borderRadius="full">
              Cancelar
            </Button>
            <Button
              size="sm"
              bg="gray.900"
              color="white"
              borderRadius="full"
              onClick={handleCreate}
              isLoading={createMut.isPending}
              isDisabled={!fornecedorNome || !valor || !!relatorio.invalido}
              _hover={{ bg: 'gray.800' }}
            >
              Adicionar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
